/* Shelter Dashboard — modalità rifugio
   Vista dedicata per chi nel profilo è marcato is_shelter=true. */
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Home as HomeIcon, Heart, Users, Truck, ClipboardList, ChevronRight,
  CheckCircle2, AlertCircle, Activity, ArrowLeft, MapPin, Clock, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section, List, Row } from "@/components/orio";

function relative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  return `${Math.floor(h / 24)}g fa`;
}

export default function ShelterDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [missions, setMissions] = useState([]);
  const [candidacies, setCandidacies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (!user.is_shelter) {
      toast.error("Sei nel profilo non come rifugio");
      navigate("/app/profilo");
      return;
    }
    (async () => {
      try {
        const [m, c] = await Promise.all([
          api.get("/missions/v2"),
          api.get("/shelter/candidacies").catch(() => ({ data: [] })),
        ]);
        setMissions(m.data.missions || []);
        setCandidacies(c.data || []);
      } catch {
        toast.error("Errore caricamento dashboard rifugio");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  // Buckets
  const toEvaluate = useMemo(
    () => missions.filter((m) => m.status === "open" && m.creator_id !== user?.id && (m.participants || []).every((p) => p.user_id !== user?.id)),
    [missions, user]
  );
  const taken = useMemo(
    () => missions.filter((m) => (m.creator_id === user?.id || (m.participants || []).some((p) => p.user_id === user?.id)) && m.status !== "closed"),
    [missions, user]
  );

  const volunteersUnique = useMemo(() => {
    const set = new Map();
    taken.forEach((m) =>
      (m.participants || []).forEach((p) => p.user_id !== user?.id && set.set(p.user_id, p))
    );
    return Array.from(set.values());
  }, [taken, user]);

  const pendingCandidacies = candidacies.filter((c) => c.status === "pending").length;

  if (loading) {
    return <div className="max-w-5xl mx-auto p-8 text-white/55 text-sm">Caricamento dashboard rifugio…</div>;
  }

  return (
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-32" data-testid="shelter-dashboard">
      <button
        onClick={() => navigate("/app/missioni")}
        data-testid="back-to-missions"
        className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="h-3 w-3" /> Centro operativo
      </button>

      <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFB020] inline-flex items-center gap-1.5 mb-2">
        <HomeIcon className="h-3 w-3" /> Modalità rifugio
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
        Dashboard <span className="orio-grad-text">{user?.shelter_name || user?.name}</span>
      </h1>
      <p className="text-white/60 mt-2 text-sm max-w-2xl">
        Tutto quello che il tuo rifugio deve gestire: missioni in attesa, missioni in carico, volontari disponibili, candidature di adozione.
      </p>

      {/* Stat strip */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard color="#FFB020" icon={ClipboardList} label="Da valutare" value={toEvaluate.length} testid="stat-to-evaluate" />
        <StatCard color="#34D399" icon={Activity} label="In carico" value={taken.length} testid="stat-taken" />
        <StatCard color="#34D399" icon={Users} label="Volontari attivi" value={volunteersUnique.length} testid="stat-volunteers" />
        <StatCard color="#FF3B30" icon={Heart} label="Candidature" value={pendingCandidacies} testid="stat-candidacies" />
      </div>

      {/* Quick actions */}
      <Section eyebrow="Operazioni" title="Azioni rapide">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <ActionTile
            icon={CheckCircle2}
            label="Prendi in carico"
            sub="Valuta una nuova missione"
            onClick={() => toEvaluate[0] && navigate(`/app/missioni/${toEvaluate[0].id}`)}
            disabled={toEvaluate.length === 0}
            accent="#34D399"
            testid="action-take"
          />
          <ActionTile
            icon={Users}
            label="Richiedi volontari"
            sub="Apri una richiesta supporto"
            onClick={() => navigate("/app/missioni?action=create")}
            accent="#FFB020"
            testid="action-volunteers"
          />
          <ActionTile
            icon={Truck}
            label="Richiedi trasporto"
            sub="Apri staffetta"
            onClick={() => navigate("/app/missioni?action=transport")}
            accent="#FFB020"
            testid="action-transport"
          />
          <ActionTile
            icon={Inbox}
            label="Candidature adozione"
            sub={`${pendingCandidacies} in attesa`}
            to="/app/rifugi/me/candidature"
            accent="#FF3B30"
            testid="action-candidacies"
          />
        </div>
      </Section>

      {/* Missions to evaluate */}
      <Section eyebrow="Da valutare" title="Missioni in attesa di un rifugio">
        {toEvaluate.length === 0 ? (
          <EmptyBlock label="Nessuna missione in attesa." sub="Quando un cittadino apre una segnalazione nella tua zona apparirà qui." />
        ) : (
          <List>
            {toEvaluate.slice(0, 6).map((m) => (
              <MissionRow key={m.id} m={m} />
            ))}
          </List>
        )}
      </Section>

      {/* Missions taken */}
      <Section eyebrow="In carico" title="Le tue missioni attive">
        {taken.length === 0 ? (
          <EmptyBlock label="Nessuna missione attiva." sub="Apri o partecipa a una missione per iniziare." />
        ) : (
          <List>
            {taken.map((m) => (
              <MissionRow key={m.id} m={m} taken />
            ))}
          </List>
        )}
      </Section>

      {/* Volunteers */}
      <Section eyebrow="Squadra" title="Volontari disponibili">
        {volunteersUnique.length === 0 ? (
          <EmptyBlock label="Nessun volontario al momento." sub="Quando qualcuno si unisce alle tue missioni apparirà qui." />
        ) : (
          <List>
            {volunteersUnique.slice(0, 10).map((p) => (
              <div key={p.user_id} className="flex items-center gap-3 px-4 sm:px-5 py-3.5">
                <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 font-medium text-[13px]"
                  style={{ background: `hsl(${(p.name?.charCodeAt(0) || 70) * 5 % 360} 50% 25%)` }}>
                  {p.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px]">{p.name}</div>
                  <div className="text-[11px] text-white/45 mt-0.5">
                    {p.role === "shelter" ? "Rifugio" : p.role === "activist" ? "Attivista" : "Cittadino"}
                    {p.skills?.length > 0 && ` · ${p.skills.slice(0, 3).join(", ")}`}
                  </div>
                </div>
                <span className="text-[10px] text-white/40">{relative(p.joined_at)}</span>
              </div>
            ))}
          </List>
        )}
      </Section>
    </div>
  );
}

/* ---------- subcomponents ---------- */

function StatCard({ color, icon: Icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-white/[0.08] p-3 sm:p-4"
      style={{
        background: `linear-gradient(160deg, ${color}14 0%, rgba(14,14,16,0.6) 70%)`,
        backdropFilter: "blur(18px)",
      }}
    >
      <div className="flex items-center justify-between">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-[10px] uppercase tracking-widest" style={{ color }}>{label}</span>
      </div>
      <div className="font-display text-3xl tabular-nums mt-2">{value}</div>
    </div>
  );
}

function ActionTile({ icon: Icon, label, sub, onClick, to, accent, disabled, testid }) {
  const inner = (
    <>
      <div
        className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${accent}1F`, color: accent, border: `1px solid ${accent}33` }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium leading-tight">{label}</div>
        <div className="text-[11px] text-white/55 mt-0.5 line-clamp-1">{sub}</div>
      </div>
    </>
  );
  const cls = `rounded-2xl p-3 flex items-center gap-3 border transition-all ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-white/[0.05]"}`;
  const style = { background: "rgba(255,255,255,0.025)", borderColor: "rgba(255,255,255,0.08)" };
  if (to && !disabled) {
    return <Link to={to} data-testid={testid} className={cls} style={style}>{inner}</Link>;
  }
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={cls}
      style={style}
    >
      {inner}
    </button>
  );
}

function MissionRow({ m, taken }) {
  const urg = m.urgency === "critical" ? { l: "Critica", c: "#FF3B30" } : m.urgency === "high" ? { l: "Alta", c: "#FFB020" } : { l: "Media", c: "#34D399" };
  return (
    <Link
      to={`/app/missioni/${m.id}`}
      data-testid={`shelter-mission-${m.id}`}
      className="flex items-center gap-3 px-4 sm:px-5 py-3.5 hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors"
    >
      <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${urg.c}1F`, color: urg.c, border: `1px solid ${urg.c}33` }}>
        {taken ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]">
          <span style={{ color: urg.c }}>{urg.l}</span>
          <span className="text-white/30">·</span>
          <span className="text-white/45 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{relative(m.created_at)}</span>
        </div>
        <div className="text-[14px] mt-0.5 line-clamp-1">{m.title}</div>
        <div className="text-[11px] text-white/55 mt-0.5 inline-flex items-center gap-2">
          {m.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{m.city}</span>}
          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{(m.participants || []).length}</span>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-white/30 shrink-0" />
    </Link>
  );
}

function EmptyBlock({ label, sub }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] p-8 text-center"
      style={{ background: "rgba(255,255,255,0.02)" }}>
      <div className="text-[13px] text-white/70">{label}</div>
      <div className="text-[11px] text-white/45 mt-1">{sub}</div>
    </div>
  );
}
